import * as React from 'react';
import { BasePageStyles, globalColors } from '../../hooks/styles';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Box, Button, Card, Checkbox, FormControl, FormControlLabel, Grid, Input, InputAdornment, InputLabel, TextField, Tooltip } from '@mui/material';
import { IMAGES_URL  } from '../../constants/Images';
import { PRODUCTS  } from '../../constants/Products';
import NumberInput from '../../components/numberPicker';
import { EvaluateExpression, RoundNumber } from '../../hooks/functions/EvaluateExpression';
import config from '../../config';
import VideocamIcon from '@mui/icons-material/Videocam';
import queryString from 'query-string';
import { Interweave, Node } from 'interweave';
import { useCart } from "react-use-cart";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { FormatCurrency } from '../../hooks/functions/FormatCurrency';


interface ProductProps {
	odrQuantity: number
	narcanQuantity: number
	odrPouchQuantity: number
	narcanChecked: boolean
	autoCalcNarcanChecked: boolean
	products: any[],
	setOdrQuantity: (quantity: number) => void
	setNarcanQuantity: (quantity: number) => void
	setOdrPouchQuantity: (quantity: number) => void
	setNarcanChecked: (isChecked: boolean) => void
	setAutoCalcNarcanChecked: (isChecked: boolean) => void
	setSnackbarMessage: (payload: { message: string; type: string, snackbarAutohide?: number }) => void;
}

export const ProductComponent: React.FC<ProductProps> = (props) => {
	const classes = BasePageStyles();
	const history = useHistory(); 
	const products = PRODUCTS;

	const { productName } = useParams<any>(); //human-readable name from url,
	
	const thisProductObj: any = productName ? products.find((p) => {
		return p.urlId === productName.toLowerCase()
	}) : null;

	const isProductPreorder = thisProductObj?.isPreorder;

	const MIN_QTY = thisProductObj?.minQty ?? 1;
	const MAX_QTY = 1000;

	const { search } = useLocation();
	const { updateItemQuantity, addItem } = useCart();
	const values = queryString.parse(search);

	const lookupAddlComponent = (additionalComponentName: string) => {
		switch(additionalComponentName) {
			
			default:
				return <></>
		}
	}

	const [curProductQuantity, setCurProductQuantity] = React.useState(MIN_QTY);
	const [narcanAddOnQuantity, setNarcanAddOnQuantity] = React.useState(0);

	const [tooltipTitle, setTooltipTitle] = React.useState(`Min ${MIN_QTY}`);
	const [narcanTooltipTitle, setNarcanTooltipTitle] = React.useState(`Min ${MIN_QTY}`);

	const [qsDiscountCode, setQsDiscountCode] = React.useState('');

	const isFirstRender = React.useRef(true);

	if (values?.discountcode && !qsDiscountCode) {
		console.log('DISCOUNT CODE:', values?.discountcode);
		setQsDiscountCode(values.discountcode as string)
	}

	const routeChange = (path: string) =>{ 
		history.push(path);
	}

	const getProductDescription = () => {
		return <Interweave content={thisProductObj?.descriptionHtml || ''} />
	}

	const onAddToCart = () => {
			if (thisProductObj) {
				addToCart(thisProductObj?.id, curProductQuantity);

				if (narcanAddOnQuantity > 0) {
					addToCart('NARCAN', narcanAddOnQuantity);
				}
			}
			else {
				console.error('No product loaded')
			}
	}

	const addToCart = (productId: string, productQty: number) => {
		let productObj: any;

			productObj = products.find((p: any) => {
				return p?.id === productId
			});

			if (!productObj) {
					console.error(`product not found: ${productId}`);
					return;		
			}	

			console.log(`adding ${productQty} of ${productId} to cart`);
			addItem(productObj, productQty); //Cart hook

		props.setSnackbarMessage({message: 'Your product(s) have been added to your cart.', type: 'success'})
	}

	const onGoToCart = () => {
		routeChange('/cart')
	}

	const onCurProductQuantityChange = (newQty: number) => {
		if(newQty < MIN_QTY) newQty = MIN_QTY
		if(newQty > MAX_QTY) newQty = MAX_QTY

		setCurProductQuantity(newQty)


		if(props.narcanChecked && props.autoCalcNarcanChecked) {		
				onNarcanQuantityChange(newQty * 2)
		}
	}

	const isODRPouch = () => {
		return Boolean(thisProductObj?.id === 'ODRPOUCH');
	}

	const isODRBox = () => {
		return Boolean(thisProductObj?.id === 'ODR_KIT');
	}

	const getOdrPouchQuantityInCurrentPage = () => {
		if (!isODRPouch()) return 0;

		return curProductQuantity
	}

	const getOdrBoxQuantityInCurrentPage = () => {
		if (!isODRBox()) return 0;

		return curProductQuantity
	}


	const onNarcanClicked = (isChecked: boolean) => {
		setNarcanAddOnQuantity(isChecked ? 
				(curProductQuantity * 2)
		: 0);

		props.setNarcanChecked(isChecked)
		props.setAutoCalcNarcanChecked(true)
	}

	const onNarcanQuantityChange = (newQty: number) => {
		if (isODRPouch()) {
			if(newQty < MIN_QTY) {
				newQty = MIN_QTY
			}
			else if(newQty > MAX_QTY) {
				newQty = MAX_QTY
			}

			if (newQty === MIN_QTY) {
				setNarcanTooltipTitle(`Min ${MIN_QTY}`)
			}
			else if (newQty === MAX_QTY) {
				setNarcanTooltipTitle(`Max ${MAX_QTY}`)
			}
		}
		else {
			if(newQty < MIN_QTY) {
				newQty = MIN_QTY
			}
			else if(newQty > MAX_QTY * 2) {
				newQty = MAX_QTY * 2
			}

			if (newQty === MIN_QTY) {
				setNarcanTooltipTitle(`Min ${MIN_QTY}`)
			}
			else if (newQty === MAX_QTY * 2) {
				setNarcanTooltipTitle(`Max ${MAX_QTY * 2}`)
			}
		}
		
		setNarcanAddOnQuantity(newQty);

		if(isFirstRender.current) isFirstRender.current = false;
	}

	const onOdrPouchQuantityChange = (newQty: number) => {
		if(newQty < MIN_QTY) newQty = MIN_QTY
		if(newQty > MAX_QTY) newQty = MAX_QTY

		setCurProductQuantity(newQty);
		
		if(props.narcanChecked && props.autoCalcNarcanChecked) {
			onNarcanQuantityChange(newQty)
		}
	}

	const onAutoCalcNarcanClicked = (isChecked: boolean) => {
		if(isChecked) {
			if (isODRPouch()) {
				onOdrPouchQuantityChange(curProductQuantity)
			}
			else {
				onNarcanQuantityChange(curProductQuantity * 2)
			}
			
		}

		props.setAutoCalcNarcanChecked(isChecked)
	}

	// React.useEffect(() => {
	// 	//View Item
	// 	window.dataLayer = window.dataLayer || [];
	// 	window.dataLayer.push({
	// 		"event": "view_item",
	// 		"ecommerce": {
	// 			"items": [{
	// 				"index": 0,
	// 				"price": ODR_KIT_PRICE,
	// 				"coupon": "",
	// 				"item_id": ODR_KIT,
	// 				"discount": 0,
	// 				"quantity": odrQuantity,
	// 				"item_name": "ODRescue Box",
	// 				"item_brand": "ODRescue",
	// 				"affiliation": "FFF Enterprises",
	// 				"item_variant": "",
	// 				"item_category": ""
	// 			},
	// 			{
	// 				"index": 0,
	// 				"price": NARCAN_PRICE,
	// 				"coupon": "",
	// 				"item_id": NARCAN_PRODUCT_ID,
	// 				"discount": 0,
	// 				"quantity": narcanQuantity,
	// 				"item_name": "NARCAN Nasal Spray 4 mg",
	// 				"item_brand": "Narcan",
	// 				"affiliation": "Narcan Shop",
	// 				"item_variant": "",
	// 				"item_category": ""
	// 			}
	// 		],
	// 			"value": EvaluateExpression(`(${odrQuantity} * ${ODR_KIT_PRICE}) + (${narcanQuantity} * ${NARCAN_PRICE})`),
	// 			"currency": "USD"
	// 		}
	// 	});
	// }, []);

	return (
		<Grid container item xs={12} lg={10} style={{
			justifyContent: 'center'
		}}>
			<Grid item xs={12} lg={5}>
				{/*<img src={thisProductObj?.fullsizeImg} style={{maxWidth: '100%'}}></img>*/}
				<div style={{marginRight: 20, maxWidth: '95%'}}>
					<div style={{marginBottom: 40}}>					
						 <ImageGallery 
						 	items={thisProductObj?.galleryImages || []} 
						 	showNav={false}
						 	showFullscreenButton={false}
						 	showPlayButton={false}
					 	/>


				 	</div>
				 	
				 	<div style={{ maxWidth: '95%'}}>
				 		<h3>
				 			Related Products
			 			</h3>
			 			<ul className={classes.relatedProductsList}>
				 			{
				 				products.map((relatedProduct) => {
				 					if (relatedProduct.id !== thisProductObj?.id) {
				 						return <li 
				 							key={relatedProduct.id} 
				 							onClick={() => {
				 								routeChange(relatedProduct.link)
				 							}}
			 							>
			 								<div style={{
			 									display: 'flex',
			 									flexDirection: 'column',
		 									}}>
		 										<div style={{height: 110, display: 'flex', alignItems: 'center'}}>
			 										<img src={relatedProduct.thumbnail} style={{
			 											maxWidth: 100,
			 											maxHeight: 110
			 										}} />
		 										</div>
		 										<span style={{
				 									display: 'flex',
				 									flexDirection: 'column'
			 									}}>
		 											<span>
				 										{relatedProduct.shortName || relatedProduct.name}
				 									</span>
				 									{relatedProduct.isComingSoon && <span style={{fontSize: 14, fontWeight: 800}}>
				 											(COMING SOON)
				 										</span>
				 									}
				 								</span>
				 							</div>
				 						</li>
				 					}
				 				})
				 			}
			 			</ul>
			 		</div>
			 	</div>
			</Grid>{/* end left col */}


			<Grid item xs={12} lg={5}>
				<h1 style={{
					marginBottom: 0
				}}>
					{isProductPreorder ? 'Pre-order' : 'Order'} {thisProductObj?.name}
				</h1>

				
					<h3 style={{
						marginTop: 0
					}}>
						{thisProductObj ? 
							( !(thisProductObj.isComingSoon)  ?  FormatCurrency(Number(thisProductObj.price)) : <h2>COMING SOON!</h2> )
						: ''}
					</h3>

				{
					Boolean(qsDiscountCode) && <h2 style={{
						color: 'green'
					}}>
						Your discount code "{qsDiscountCode}" will be applied on the checkout page.
					</h2>
				}

					<Grid container item xs={12} md={12}>
							<Box sx={{paddingLeft: 0}}>

							<Grid container item xs={12} md={12} sx={{display: 'flex', justifyContent: 'space-between'}}>

								{thisProductObj?.additionalComponentName && 
										<Grid item xs={12} md={12} style={{
											paddingTop: 18
										}}>
											{getProductDescription()}
										
									</Grid>
								}

								{
									//*******************************************************************************
									//if a custom component is needed for addl functionality (ie Build a box), render here!
									thisProductObj?.additionalComponentName && lookupAddlComponent(thisProductObj.additionalComponentName)
								}

								{!(thisProductObj?.isComingSoon) && <>
										{<Grid>

											{thisProductObj?.hasNarcanAddOn && props.narcanChecked && <h3>
												{thisProductObj?.name}
											</h3>}

											{/*<Tooltip title={tooltipTitle} open={isTooltipOpen()} PopperProps={{ disablePortal: true }} arrow> */}
											
												<NumberInput aria-label="Quantity Input" min={MIN_QTY} max={MAX_QTY} 
													title={''}
													value={curProductQuantity}
													onChange={(event, newValue) => {
														onCurProductQuantityChange(newValue || 0)
													}}
													onInputChange={(event) => {
														onCurProductQuantityChange(parseInt(event.target.value || '0'))
													}}
													endAdornment={<InputAdornment position="end" sx={{marginLeft: '-35px', marginRight: '18px'}}>
														{thisProductObj?.shortQtyDescriptor}
													</InputAdornment>}
												/>
											
											{/*</Tooltip> */}
										</Grid>}

										{thisProductObj?.hasNarcanAddOn && props.narcanChecked && 
											<Grid sx={{maxWidth: '50%'}}>
												{/*<Tooltip title={narcanTooltipTitle} open={isNarcanTooltipOpen()} PopperProps={{ disablePortal: true }} arrow>*/}
													<NumberInput aria-label="Quantity Input" min={MIN_QTY} max={isODRPouch() ? MAX_QTY : MAX_QTY*2} 
														title={'Narcan\u00AE Nasal Spray ($34.97/box)'}
														value={narcanAddOnQuantity}
														onChange={(event, newValue) => {onNarcanQuantityChange(newValue || 0)}}
														onInputChange={(event) => {onNarcanQuantityChange(parseInt(event.target.value || '0'))}}
														endAdornment={<InputAdornment position="end" sx={{marginLeft: '-35px', marginRight: '18px'}}>bx</InputAdornment>}
														disabled={props.autoCalcNarcanChecked}
													/>
												{/*</Tooltip> */}
												<FormControlLabel
													label={(<span style={{fontSize: 13, lineHeight: '10px'}}>{isODRPouch() ? 'One box' : 'Two boxes'} of NARCAN&reg; per Wall Unit</span>)}
													className={classes.termsCheck}
													sx={{display: 'flex', justifyContent: 'center', marginTop: '18px'}}
													control={
														<Checkbox
															checked={props.autoCalcNarcanChecked}
															onChange={(event, isChecked) => {onAutoCalcNarcanClicked(isChecked)}}
															inputProps={{ 'aria-label': 'controlled' }}
															size={'small'}
														/>
													}
												/>
													
											</Grid>
										}
									</>
								}
							</Grid>
							
								{
									!(thisProductObj?.isComingSoon) && thisProductObj?.hasNarcanAddOn && 
										<Grid item xs={12} sx={{display: 'flex'}}>
											<FormControlLabel
												label={(<span>Add NARCAN&reg; Nasal Spray 4mg</span>)}
												className={classes.termsCheck}
												control={
													<Checkbox
														checked={props.narcanChecked}
														onChange={(event, isChecked) => {onNarcanClicked(isChecked)}}
														inputProps={{ 'aria-label': 'controlled' }}
													/>
												}
											/>
										</Grid>
								}

								{!(thisProductObj?.isComingSoon) && 
									<Grid  item xs={12} sx={{ marginTop: '20px'}}>
										<Button variant="contained" size='large'
											onClick={onAddToCart}
											
										>
											Add To Cart
										</Button>

										<Button variant="contained" size='large'
											style={{marginLeft: 18}}
											onClick={onGoToCart}
										>View Cart</Button>
									</Grid>
								}

						
							</Box>

							{!thisProductObj?.additionalComponentName && 
								<Grid item xs={12} md={12} style={{
									paddingTop: 18
								}}>
									{getProductDescription()}
								
							</Grid>
						}
						</Grid>
			</Grid>{/* end right col */}
		
			<Grid item xs={12} sx={{textAlign: 'center', paddingTop: '50px'}}>
				<p>NARCAN&reg; is a registered trademark of Emergent Operations Ireland Limited.</p>
			</Grid>
		</Grid>
	)
}





